import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Puzzle, Play, Loader2, Wifi, WifiOff, Repeat, Bookmark, Globe, ListChecks,
  MonitorSmartphone, RefreshCw, AlertTriangle, Search, X,
  Clock, Bug, ChevronRight, ChevronLeft, FileSpreadsheet, Activity, Settings2,
  Gauge, Download, Chrome, Server, CheckCircle2, CircleDot, Upload,
} from "lucide-react";
import {
  ImportCasesTab, RunBatchModal, RunningTab, ExtSettingsTab, notifyDone,
} from "@/components/dashboard/ExtensionBatch";
import Aurora from "@/components/common/Aurora";
import EmptyState from "@/components/shared/EmptyState";
import StepTimeline from "@/components/shared/StepTimeline";
import Modal from "@/components/dashboard/Modal";
import { StatusBadge } from "@/components/ui/Badge";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { authedBlobUrl } from "@/api/client";
import {
  listDevices, startExtRun, getExtRun, listExtTests, replayTest,
  isTerminalExt, deleteAllExtTests, listExtBatches, listExtRuns,
  getExtBatchItems, downloadExtBatchReport,
  getExtensionInfo, downloadExtension,
} from "@/api/extension";
import DeleteAllButton from "@/components/shared/DeleteAllButton";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/dates";

const TABS = [
  { key: "health", label: "System Health", icon: Gauge },
  { key: "run", label: "Run Single", icon: Play },
  { key: "cases", label: "Run batch", icon: FileSpreadsheet },
  { key: "imports", label: "Imports", icon: Upload },
  { key: "running", label: "Running", icon: Activity },
  { key: "saved", label: "Saved Tests", icon: Bookmark },
  { key: "history", label: "History", icon: ListChecks },
  { key: "settings", label: "Settings", icon: Settings2 },
];

const STATUS_DOT = {
  PASS: "bg-emerald-500", FAIL: "bg-rose-500", ERROR: "bg-amber-500",
  INCONCLUSIVE: "bg-slate-400",
};

const isBatchDone = (s) => ["completed", "stopped"].includes(String(s || "").toLowerCase());

// Global batch-completion watcher. Polls the batch list regardless of the active
// tab and pops a browser notification the moment any batch flips to
// completed/stopped — so a finish is announced even when the user isn't sitting
// on that batch's Running tab. Tracks the last-seen status per batch; the first
// poll only seeds the map (no notifications for already-finished batches). Backs
// off while the tab is hidden and never overlaps in-flight requests.
function useBatchDoneNotifier() {
  const seen = useRef(null);      // Map<batchId, status> | null (null = not yet seeded)
  const inFlight = useRef(false);
  useEffect(() => {
    try {
      if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    } catch (e) { /* ignore */ }

    let stopped = false;
    let handle = null;

    const schedule = () => {
      if (stopped) return;
      const hidden = typeof document !== "undefined" && document.hidden;
      handle = setTimeout(tick, hidden ? 15000 : 6000);
    };

    const tick = async () => {
      if (stopped) return;
      if (inFlight.current) { schedule(); return; }
      inFlight.current = true;
      try {
        const list = await listExtBatches(50);
        if (stopped) return;
        const seeding = seen.current === null;
        if (seeding) seen.current = new Map();
        for (const b of list) {
          const prev = seen.current.get(b.id);
          if (!seeding && prev && !isBatchDone(prev) && isBatchDone(b.status)) notifyDone(b);
          seen.current.set(b.id, b.status);
        }
      } catch (e) { /* ignore transient blips */ }
      finally { inFlight.current = false; schedule(); }
    };

    tick();
    return () => { stopped = true; clearTimeout(handle); };
  }, []);
}

function useDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { setDevices(await listDevices()); } catch (e) { setDevices([]); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // status flips as panels open/close
    return () => clearInterval(t);
  }, []);
  return { devices, loading, reload: load };
}

function useRunWatch() {
  const [run, setRun] = useState(null);
  const timer = useRef(null);
  const watch = (runId) => {
    clearInterval(timer.current);
    const tick = async () => {
      try {
        const r = await getExtRun(runId);
        setRun(r);
        if (isTerminalExt(r.status)) clearInterval(timer.current);
      } catch (e) { /* keep polling */ }
    };
    tick();
    timer.current = setInterval(tick, 1500);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return { run, watch, reset: () => { clearInterval(timer.current); setRun(null); } };
}

// Per-step screenshot thumbnail + lightbox. Fetches the auth-protected extension
// endpoint as a blob (a plain <img> can't send the Bearer token).
function ExtShot({ runId, stepIndex }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true, created = null;
    authedBlobUrl(`/extension/runs/${runId}/screenshots/${stepIndex}`).then((u) => {
      if (!active) { if (u) URL.revokeObjectURL(u); return; }
      created = u; setUrl(u);
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [runId, stepIndex]);
  if (!url) return null;
  return (
    <>
      <button
        type="button" onClick={() => setOpen(true)} title="View screenshot"
        className="grid h-12 w-16 shrink-0 cursor-zoom-in overflow-hidden rounded-lg bg-slate-100 dark:bg-white/10"
      >
        <img src={url} alt="screenshot" className="h-full w-full object-cover" />
      </button>
      {open && (
        <div onClick={() => setOpen(false)} className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/85 p-6">
          <button type="button" aria-label="Close" className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-white"><X size={20} /></button>
          <img src={url} alt="screenshot" className="max-h-[85vh] max-w-full rounded-xl shadow-glow" />
        </div>
      )}
    </>
  );
}

function StepRow({ runId, step, i }) {
  const status = String(step.status || "PASS").toUpperCase();
  return (
    <li className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3 dark:border-white/5 dark:bg-white/5">
      <span className={"mt-1 h-2.5 w-2.5 shrink-0 rounded-full " + (STATUS_DOT[status] || "bg-slate-300")} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium capitalize text-slate-800 dark:text-white">
          {step.action} <span className="ml-1 text-[0.625rem] font-semibold uppercase tracking-wide text-slate-400">{status}</span>
        </p>
        {step.reason && <p className="mt-0.5 break-words text-xs text-slate-500 dark:text-slate-400">{step.reason}</p>}
        {step.selector_used && <p className="mt-0.5 truncate font-mono text-[0.6875rem] text-slate-400">{step.selector_used}</p>}
        {Array.isArray(step.findings) && step.findings.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {step.findings.map((f, fi) => (
              <li key={fi} className="flex items-start gap-1.5 text-xs">
                <span className={"mt-0.5 shrink-0 rounded px-1 text-[0.625rem] font-semibold uppercase " + (
                  f.severity === "high" ? "bg-rose-500/15 text-rose-600 dark:text-rose-300"
                    : f.severity === "medium" ? "bg-amber-500/15 text-amber-600 dark:text-amber-300"
                    : "bg-slate-500/15 text-slate-500 dark:text-slate-300")}>{f.severity || "low"}</span>
                <span className="text-slate-600 dark:text-slate-300">
                  <b>{f.title}</b>{f.area ? ` (${f.area})` : ""}{f.detail ? ` — ${f.detail}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {step.has_screenshot && <ExtShot runId={runId} stepIndex={step.index ?? i} />}
    </li>
  );
}

function RunDetailModal({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    getExtRun(runId).then((r) => { if (active) { setRun(r); setLoading(false); } }).catch(() => active && setLoading(false));
    return () => { active = false; };
  }, [runId]);

  const steps = (run && run.steps) || [];
  const h = run && run.health;

  return (
    <Modal open onClose={onClose} title="Run detail" size="xl">
      {loading ? (
        <div className="flex justify-center py-10 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : !run ? (
        <p className="text-sm text-slate-400">Could not load this run.</p>
      ) : (
        <div className="space-y-5">
          {/* header */}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={run.status} />
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">{run.source}{run.is_replay ? " · replay" : ""}</span>
              {run.duration_seconds != null && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock size={12} /> {Number(run.duration_seconds).toFixed(1)}s</span>
              )}
              {run.token_usage && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-white/10 dark:text-slate-300">
                  ${Number(run.token_usage.estimated_cost_usd || 0).toFixed(4)}
                  <span className="text-slate-400"> · {((run.token_usage.total_input_tokens || 0) + (run.token_usage.total_output_tokens || 0)).toLocaleString()} tokens</span>
                </span>
              )}
            </div>
            <p className="mt-2 text-sm font-medium text-slate-800 dark:text-white">{run.instruction}</p>
            {run.url && <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-slate-400"><Globe size={12} /> {run.url}</p>}
          </div>

          {/* health */}
          {h && (
            <div className="rounded-xl border border-slate-100 bg-white p-3 dark:border-white/5 dark:bg-white/5">
              <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">Page health</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-300 sm:grid-cols-4">
                <span>Console errors: <b>{(h.console_errors || []).length}</b></span>
                <span>JS errors: <b>{(h.js_errors || []).length}</b></span>
                <span>Network fails: <b>{(h.network_failures || []).length}</b></span>
                {h.performance && h.performance.load_ms != null && <span>Load: <b>{h.performance.load_ms} ms</b></span>}
              </div>
            </div>
          )}

          {/* bug report */}
          {run.bug_report && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-3 text-sm dark:border-rose-500/20 dark:bg-rose-500/10">
              <p className="flex items-center gap-1.5 font-semibold text-rose-700 dark:text-rose-200"><Bug size={14} /> {run.bug_report.title}</p>
              {run.bug_report.description && <p className="mt-1 text-rose-700/90 dark:text-rose-300/90">{run.bug_report.description}</p>}
            </div>
          )}

          {/* steps + screenshots */}
          <div>
            <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
              {steps.length} step{steps.length === 1 ? "" : "s"}
            </p>
            {steps.length === 0 ? (
              <p className="text-sm text-slate-400">No steps recorded.</p>
            ) : (
              <ul className="space-y-2">
                {steps.map((s, i) => <StepRow key={s.index ?? i} runId={run.id} step={s} i={i} />)}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function DevicePicker({ devices, value, onChange }) {
  const online = devices.filter((d) => d.status === "online");
  if (!online.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
        <WifiOff size={15} /> No browser connected. Open the extension side panel to bring a device online.
      </div>
    );
  }
  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Run in</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
      >
        {online.map((d) => (
          <option key={d.id} value={d.id}>{d.label || d.id.slice(0, 8)} — online</option>
        ))}
      </select>
    </div>
  );
}

// Human-readable bytes for the extension build size.
function fmtBytes(b) {
  if (b == null) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

// System Health tab — mirrors the Exe Agent's System Health: the extension build
// (download + version/size/updated) plus the live "Connected browsers" list
// (which browsers are online right now). The rebuild command lives on the
// Developer dashboard → Build tools; testers download builds, they don't make them.
function ExtSystemHealthTab({ devices, reloadDevices }) {
  const [info, setInfo] = useState(null);      // { available, version, size_bytes, released_at }
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState(null);        // { type: "ok"|"err", text }

  useEffect(() => {
    let dead = false;
    setLoadingInfo(true);
    getExtensionInfo()
      .then((d) => !dead && setInfo(d))
      .catch(() => !dead && setInfo({ available: false }))
      .finally(() => !dead && setLoadingInfo(false));
    return () => { dead = true; };
  }, []);

  const online = devices.filter((d) => d.status === "online").length;
  const notBuilt = !loadingInfo && info && !info.available;
  const size = fmtBytes(info && info.size_bytes);

  const handleDownload = async () => {
    setDownloading(true); setMsg(null);
    try {
      const r = await downloadExtension();
      setMsg({ type: "ok", text: `Saved ${r.filename} (${r.sizeLabel})` });
    } catch (e) {
      setMsg({ type: "err", text: e.message || "Download failed." });
    } finally {
      setDownloading(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  return (
    <div className="space-y-4">
      {/* Extension build */}
      <div className="card overflow-hidden">
        <div className="border-b border-slate-100 bg-gradient-to-br from-brand-50 to-white px-5 py-4 dark:border-white/10 dark:from-brand-500/10 dark:to-ink-900">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-glow">
              <Chrome size={18} />
            </span>
            <div>
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-300">Chrome extension</p>
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">AI-QA-Engine Extension</h3>
            </div>
          </div>
        </div>
        <div className="p-5">
          <p className="max-w-xl text-sm text-slate-600 dark:text-slate-300">
            Download the extension, load it unpacked in <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-white/10">chrome://extensions</code>,
            and paste your access token in the side panel to bring this browser online.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button size="sm" onClick={handleDownload} disabled={loadingInfo || downloading || notBuilt}>
              {downloading
                ? <><Loader2 size={15} className="animate-spin" /> Preparing…</>
                : <><Download size={15} /> Download Extension</>}
            </Button>
            {loadingInfo && <span className="text-xs text-slate-400">Checking build…</span>}
            {info && info.available && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {info.version && <>v{info.version}</>}
                {size && <> · {size}</>}
                {info.released_at && (
                  <>
                    {" · "}
                    <span title={formatDateTime(info.released_at)}>
                      Updated {formatDateTime(info.released_at)}
                    </span>
                  </>
                )}
              </span>
            )}
            {notBuilt && (
              <span className="text-xs font-medium text-amber-600 dark:text-amber-300">
                Build pending — run the command below, then commit &amp; push.
              </span>
            )}
          </div>

          {msg && (
            <p className={cn(
              "mt-3 inline-flex items-center gap-1.5 text-xs font-medium",
              msg.type === "ok" ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"
            )}>
              {msg.type === "ok" ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {msg.text}
            </p>
          )}

          {/* The developer rebuild command lives on the Developer dashboard
              (Build tools) — testers download the extension, they don't build it. */}
        </div>
      </div>

      {/* Device stats */}
      <div className="grid grid-cols-1 gap-3 xs:grid-cols-3">
        {[
          { label: "Browsers", value: devices.length, icon: Server, tone: "text-brand-500 bg-brand-500/10" },
          { label: "Online", value: online, icon: CheckCircle2, tone: "text-emerald-500 bg-emerald-500/10" },
          { label: "Offline", value: devices.length - online, icon: CircleDot, tone: "text-slate-400 bg-slate-500/10" },
        ].map((s) => (
          <div key={s.label} className="card flex items-center gap-3 p-4">
            <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", s.tone)}><s.icon size={16} /></span>
            <div>
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Connected browsers */}
      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-800 dark:text-white">Connected browsers</p>
          <button onClick={reloadDevices} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
        {devices.length === 0 ? (
          <EmptyState icon={MonitorSmartphone} title="No browsers yet" message="Open the extension side panel and connect with your token to register this browser." />
        ) : (
          <ul className="space-y-2">
            {devices.map((d) => {
              const isOnline = d.status === "online";
              return (
                <li key={d.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3 dark:border-white/5 dark:bg-white/5">
                  <div className="flex items-center gap-2.5">
                    <span className={cn("relative grid h-8 w-8 shrink-0 place-items-center rounded-lg", isOnline ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" : "bg-slate-500/10 text-slate-400")}>
                      <MonitorSmartphone size={15} />
                      <span className={cn("absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-ink-900", isOnline ? "bg-emerald-500" : "bg-slate-300 dark:bg-white/20")} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{d.label || d.id.slice(0, 12)}</p>
                      {d.last_seen && <p className="text-xs text-slate-400">last seen {fmtWhen(d.last_seen)}</p>}
                    </div>
                  </div>
                  <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", isOnline ? "text-emerald-600 dark:text-emerald-300" : "text-slate-400")}>
                    <span className={cn("h-1.5 w-1.5 rounded-full", isOnline ? "bg-emerald-500" : "bg-slate-300 dark:bg-white/20")} /> {d.status}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function LiveRun({ run }) {
  if (!run) return null;
  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-ink-900">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800 dark:text-white">{run.instruction}</p>
        {isTerminalExt(run.status)
          ? <StatusBadge status={run.status} />
          : <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><Loader2 size={13} className="animate-spin" /> {run.status}</span>}
      </div>
      {(run.steps || []).length > 0 && <StepTimeline steps={run.steps} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Imports — a permanent, batch-wise record of every import/run for the
//  EXTENSION flow. Built from the extension BATCHES (which survive "Delete all"
//  on the Run batch tab, that only clears imported cases), so the history and
//  its reports stay available even after the imported cases are removed. Uses
//  the /extension/* endpoints only — kept separate from the Exe Agent imports.
// ---------------------------------------------------------------------------
const EXT_BATCH_STATUS_TONE = {
  passed: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300",
  failed: "text-rose-600 bg-rose-500/10 dark:text-rose-300",
  error: "text-amber-600 bg-amber-500/10 dark:text-amber-300",
  cancelled: "text-slate-500 bg-slate-500/10 dark:text-slate-400",
};
const fmtWhen = (d) => formatDateTime(d);

function ExtImportsTab({ onOpenRun }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [items, setItems] = useState({});       // batchId -> items[]
  const [busyId, setBusyId] = useState(null);

  const reload = () => {
    setLoading(true);
    listExtBatches(50)
      .then((res) => setBatches(Array.isArray(res) ? res : (res?.batches || [])))
      .catch((e) => toastError(e?.message || "Couldn't load imports."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!items[id]) {
      setBusyId(id);
      try {
        const it = await getExtBatchItems(id);
        setItems((s) => ({ ...s, [id]: Array.isArray(it) ? it : (it?.items || []) }));
      } catch (e) {
        toastError(e?.message || "Couldn't load the cases for this import.");
      } finally { setBusyId(null); }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>;
  }
  if (!batches.length) {
    return <EmptyState icon={Upload} title="No imports yet"
      message="Import an .xlsx on the Run batch tab and run it — every import shows up here permanently." />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Every import and its results — kept even after you delete the imported cases.
        </p>
        <Button variant="outline" size="sm" onClick={reload}><RefreshCw size={13} /> Refresh</Button>
      </div>

      {batches.map((b) => {
        const open = openId === b.id;
        const rows = items[b.id] || [];
        return (
          <div key={b.id} className="card overflow-hidden">
            <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <button type="button" onClick={() => toggle(b.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
                  <FileSpreadsheet size={16} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-slate-900 dark:text-white">
                    {b.name || `Batch ${String(b.id).slice(0, 8)}`}
                  </span>
                  <span className="block text-xs text-slate-400">
                    {fmtWhen(b.created_at)} · {b.total} case{b.total === 1 ? "" : "s"} · {b.status}
                  </span>
                </span>
                {open ? <ChevronLeft size={16} className="ml-auto shrink-0 rotate-90 text-slate-400" />
                      : <ChevronRight size={16} className="ml-auto shrink-0 rotate-90 text-slate-400" />}
              </button>

              <div className="flex flex-wrap items-center gap-2">
                {[["passed", b.passed], ["failed", b.failed], ["error", b.errored]].map(([k, v]) => (
                  <span key={k} className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", EXT_BATCH_STATUS_TONE[k])}>
                    {v || 0} {k}
                  </span>
                ))}
                <Button variant="outline" size="sm"
                  onClick={() => downloadExtBatchReport(b.id).catch((e) => toastError(e?.message || "Couldn't download the report."))}>
                  <Download size={13} /> Report
                </Button>
              </div>
            </div>

            {open && (
              <div className="border-t border-slate-100 px-4 py-3 dark:border-white/10">
                {busyId === b.id ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
                    <Loader2 size={14} className="animate-spin" /> Loading cases…
                  </div>
                ) : rows.length === 0 ? (
                  <p className="py-3 text-sm text-slate-400">No cases recorded for this import.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-slate-400">
                        <tr>
                          <th className="py-1.5 pr-3 font-medium">#</th>
                          <th className="py-1.5 pr-3 font-medium">Test case</th>
                          <th className="py-1.5 pr-3 font-medium">Status</th>
                          <th className="py-1.5 pr-3 font-medium">Device</th>
                          <th className="py-1.5 pr-3 font-medium">Time</th>
                          <th className="py-1.5 font-medium text-right">Detail</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                        {rows.map((it, idx) => {
                          const openable = !!it.run_id && typeof onOpenRun === "function";
                          return (
                            <tr key={it.id}
                              onClick={openable ? () => onOpenRun(it.run_id) : undefined}
                              className={openable ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5" : ""}>
                              <td className="py-1.5 pr-3 text-slate-400">{(it.order_index ?? idx) + 1}</td>
                              <td className="py-1.5 pr-3 text-slate-700 dark:text-slate-200">
                                {it.name}{it.is_replay ? " ⚡" : ""}
                                {it.deleted_case && (
                                  <span className="ml-1.5 text-[0.625rem] text-slate-400" title="The imported case was deleted; recovered from the run">
                                    (case deleted)
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 pr-3">
                                <span className={cn("rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold",
                                  EXT_BATCH_STATUS_TONE[it.status] || "text-slate-500 bg-slate-500/10")}>
                                  {it.status}
                                </span>
                              </td>
                              <td className="py-1.5 pr-3 text-slate-500 dark:text-slate-400">{it.device || "—"}</td>
                              <td className="py-1.5 pr-3 text-slate-400">
                                {it.duration_seconds ? `${Number(it.duration_seconds).toFixed(1)}s` : "—"}
                              </td>
                              <td className="py-1.5 text-right">
                                {openable
                                  ? <span className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-300">View <ChevronRight size={12} /></span>
                                  : <span className="text-slate-300 dark:text-slate-600">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  History — a persistent, batch-first explorer of every run. Reads the
//  extension BATCHES (which survive deleting imported cases and run history on
//  the other tabs), so past results stay browsable. Search + status filter +
//  newest/oldest sort; expand a batch to its cases; open a case for its steps
//  and screenshots. Intentionally has NO destructive "delete all" — it's the
//  permanent record.
// ---------------------------------------------------------------------------
function ExtHistoryTab({ onOpenRun }) {
  const [batches, setBatches] = useState([]);
  const [singles, setSingles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [items, setItems] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("newest");

  const reload = () => {
    setLoading(true);
    // Two sources: batches, plus the single runs that no batch item owns. This tab
    // used to read batches alone, so a Run Single run left no trace in History at
    // all. Settled independently — one failing must not blank the other.
    Promise.allSettled([listExtBatches(50), listExtRuns(50, { standalone: true })])
      .then(([b, r]) => {
        if (b.status === "fulfilled") setBatches(Array.isArray(b.value) ? b.value : (b.value?.batches || []));
        else toastError(b.reason?.message || "Couldn't load batch history.");
        if (r.status === "fulfilled") setSingles(Array.isArray(r.value) ? r.value : (r.value?.items || []));
        else toastError(r.reason?.message || "Couldn't load single-run history.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!items[id]) {
      setBusyId(id);
      try {
        const it = await getExtBatchItems(id);
        setItems((s) => ({ ...s, [id]: Array.isArray(it) ? it : (it?.items || []) }));
      } catch (e) {
        toastError(e?.message || "Couldn't load the cases for this run.");
      } finally { setBusyId(null); }
    }
  };

  const statusOptions = useMemo(
    () => Array.from(new Set(
      [...batches, ...singles].map((e) => String(e.status || "").toLowerCase()).filter(Boolean)
    )),
    [batches, singles]
  );

  // Batches and single runs interleaved by time, so a single run appears where it
  // ran instead of in a separate section below the fold.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const has = (text) => !q || String(text || "").toLowerCase().includes(q);
    const entries = [
      ...batches.map((b) => ({ kind: "batch", key: `b-${b.id}`, at: b.created_at, data: b })),
      ...singles.map((r) => ({ kind: "run", key: `r-${r.id}`, at: r.created_at, data: r })),
    ];
    return entries
      .filter((e) => {
        if (status !== "all" && String(e.data.status || "").toLowerCase() !== status) return false;
        if (e.kind === "batch") {
          return has(e.data.name || `Batch ${String(e.data.id).slice(0, 8)}`) || has(e.data.id);
        }
        return has(e.data.instruction) || has(e.data.url) || has(e.data.id);
      })
      .sort((a, b) => {
        const da = new Date(a.at || 0).getTime();
        const db = new Date(b.at || 0).getTime();
        return sort === "newest" ? db - da : da - db;
      });
  }, [batches, singles, query, status, sort]);

  return (
    <div className="card p-5">
      {/* filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input aria-label="Search batches and runs" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search history…"
            className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
          <option value="all">All statuses</option>
          {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        <button onClick={reload} className="inline-flex h-10 items-center gap-1.5 rounded-xl px-2.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
      ) : visible.length === 0 ? (
        <EmptyState icon={ListChecks} title={batches.length + singles.length ? "No runs match your filters" : "No history yet"}
          message={batches.length + singles.length ? "Try clearing the search or status filter." : "Run a single test or a batch and it shows up here permanently — with each case's steps and screenshots."} />
      ) : (
        <div className="space-y-3">
          {visible.map((entry) => {
            // A single run has no cases to expand — the card opens its own detail.
            if (entry.kind === "run") {
              const r = entry.data;
              const openable = typeof onOpenRun === "function";
              return (
                <button key={entry.key} type="button" disabled={!openable}
                  onClick={openable ? () => onOpenRun(r.id) : undefined}
                  className={cn("flex w-full items-center gap-3 rounded-xl border border-slate-100 p-4 text-left transition-colors dark:border-white/10",
                    openable && "hover:border-brand-300 dark:hover:border-brand-500/40")}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"><Play size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-semibold text-slate-900 dark:text-white">{r.instruction || r.url || `Run ${String(r.id).slice(0, 8)}`}</span>
                      <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">single run</span>
                      {r.is_replay && <span title="Replay of an earlier run" className="shrink-0 text-slate-400">⚡</span>}
                    </span>
                    <span className="block truncate text-xs text-slate-400">
                      {fmtWhen(r.created_at)}
                      {r.duration_seconds != null ? ` · ${Number(r.duration_seconds).toFixed(1)}s` : ""}
                      {r.url ? ` · ${r.url}` : ""}
                    </span>
                  </span>
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold",
                    EXT_BATCH_STATUS_TONE[String(r.status || "").toLowerCase()] || "text-slate-500 bg-slate-500/10")}>{r.status}</span>
                  {openable && <span className="shrink-0 text-slate-400"><ChevronRight size={16} /></span>}
                </button>
              );
            }
            const b = entry.data;
            const open = openId === b.id;
            const rows = items[b.id] || [];
            return (
              <div key={entry.key} className="rounded-xl border border-slate-100 dark:border-white/10">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" onClick={() => toggle(b.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
                      <ListChecks size={16} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-slate-900 dark:text-white">{b.name || `Batch ${String(b.id).slice(0, 8)}`}</span>
                      <span className="block text-xs text-slate-400">{fmtWhen(b.created_at)} · {b.total} case{b.total === 1 ? "" : "s"} · {b.status}</span>
                    </span>
                    <span className={cn("ml-auto shrink-0 text-slate-400 transition-transform", open && "rotate-90")}><ChevronRight size={16} /></span>
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    {[["passed", b.passed], ["failed", b.failed], ["error", b.errored]].map(([k, v]) => (
                      <span key={k} className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", EXT_BATCH_STATUS_TONE[k])}>{v || 0} {k}</span>
                    ))}
                    <Button variant="outline" size="sm"
                      onClick={() => downloadExtBatchReport(b.id).catch((e) => toastError(e?.message || "Couldn't download the report."))}>
                      <Download size={13} /> Report
                    </Button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-slate-100 px-4 py-3 dark:border-white/10">
                    {busyId === b.id ? (
                      <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Loading cases…</div>
                    ) : rows.length === 0 ? (
                      <p className="py-3 text-sm text-slate-400">No cases recorded for this run.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="text-slate-400">
                            <tr>
                              <th className="py-1.5 pr-3 font-medium">#</th>
                              <th className="py-1.5 pr-3 font-medium">Test case</th>
                              <th className="py-1.5 pr-3 font-medium">Status</th>
                              <th className="py-1.5 pr-3 font-medium">Device</th>
                              <th className="py-1.5 pr-3 font-medium">Time</th>
                              <th className="py-1.5 text-right font-medium">Detail</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                            {rows.map((it, idx) => {
                              const openable = !!it.run_id && typeof onOpenRun === "function";
                              return (
                                <tr key={it.id}
                                  onClick={openable ? () => onOpenRun(it.run_id) : undefined}
                                  className={openable ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5" : ""}>
                                  <td className="py-1.5 pr-3 text-slate-400">{(it.order_index ?? idx) + 1}</td>
                                  <td className="py-1.5 pr-3 text-slate-700 dark:text-slate-200">{it.name}{it.is_replay ? " ⚡" : ""}</td>
                                  <td className="py-1.5 pr-3">
                                    <span className={cn("rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold", EXT_BATCH_STATUS_TONE[it.status] || "text-slate-500 bg-slate-500/10")}>{it.status}</span>
                                  </td>
                                  <td className="py-1.5 pr-3 text-slate-500 dark:text-slate-400">{it.device || "—"}</td>
                                  <td className="py-1.5 pr-3 text-slate-400">{it.duration_seconds ? `${Number(it.duration_seconds).toFixed(1)}s` : "—"}</td>
                                  <td className="py-1.5 text-right">
                                    {openable
                                      ? <span className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-300">View <ChevronRight size={12} /></span>
                                      : <span className="text-slate-300 dark:text-slate-600">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ExtensionRunner() {
  const [tab, setTab] = useState("health");
  const { devices, reload: reloadDevices } = useDevices();
  const { run, watch, reset } = useRunWatch();
  useBatchDoneNotifier(); // announce batch completions across all tabs

  const [deviceId, setDeviceId] = useState("");
  const [form, setForm] = useState({ url: "", instruction: "" });
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState(null);

  const [tests, setTests] = useState([]);
  const [detailId, setDetailId] = useState(null);

  // batch: run modal + which batch the Running tab watches
  const [runModalCaseIds, setRunModalCaseIds] = useState(null);
  const [watchBatchId, setWatchBatchId] = useState(null);

  useEffect(() => {
    const online = devices.filter((d) => d.status === "online");
    if (online.length && !online.some((d) => d.id === deviceId)) setDeviceId(online[0].id);
  }, [devices]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTests = async () => {
    try { setTests(await listExtTests()); } catch (e) { setTests([]); }
  };

  useEffect(() => {
    if (tab === "saved") loadTests();
  }, [tab]);

  async function startRun() {
    setErr(null);
    let url = form.url.trim();
    const instruction = form.instruction.trim();
    if (!url || !instruction) { setErr("URL and instruction are both required."); return; }
    if (!deviceId) { setErr("No connected browser to run in."); return; }
    // Accept a bare domain like "cp.localbhai.com" — add the scheme so it opens.
    if (!/^https?:\/\//i.test(url)) {
      url = (/^(localhost|127\.0\.0\.1)/i.test(url) ? "http://" : "https://") + url;
    }
    setStarting(true);
    reset();
    try {
      const { run_id } = await startExtRun({ url, instruction, source: "dashboard", device_id: deviceId });
      watch(run_id);
    } catch (e) {
      setErr(e?.message || "Could not start the run.");
    } finally {
      setStarting(false);
    }
  }

  async function runReplay(testId) {
    if (!deviceId) { setTab("run"); setErr("Connect a browser, then replay."); return; }
    try {
      const { run_id } = await replayTest(testId, deviceId);
      setTab("run");
      watch(run_id);
    } catch (e) {
      setTab("run");
      setErr(e?.message || "Replay failed (is the extension online?).");
    }
  }

  const onlineCount = devices.filter((d) => d.status === "online").length;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900"
      >
        <Aurora className="opacity-60" />
        <div className="relative flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
            <Puzzle size={20} />
          </span>
          <div>
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-300">Headed testing</p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Extension</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Command a connected browser to run tests live in a real tab.{" "}
              <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
                <Wifi size={12} /> {onlineCount} online
              </span>
            </p>
          </div>
        </div>
      </motion.div>

      {/* tabs */}
      <div className="mb-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium transition-colors " +
              (tab === t.key
                ? "bg-brand-600 text-white shadow-soft"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300")
            }
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "run" && (
        <div className="card p-5">
          <DevicePicker devices={devices} value={deviceId} onChange={setDeviceId} />
          <div className="mt-4 space-y-4">
            <Input label="URL" icon={Globe} placeholder="https://your-app.com" value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
            <div>
              <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Instruction</span>
              <textarea
                rows={3} value={form.instruction}
                onChange={(e) => setForm((f) => ({ ...f, instruction: e.target.value }))}
                placeholder="Describe the test in plain English…"
                className="w-full resize-none rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </div>
            {err && (
              <p className="flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
                <AlertTriangle size={14} className="shrink-0" /> {err}
              </p>
            )}
            <Button onClick={startRun} disabled={starting || !onlineCount}>
              {starting ? <><Loader2 size={15} className="animate-spin" /> Starting…</> : <><Play size={15} /> Run in browser</>}
            </Button>
          </div>
          <LiveRun run={run} />
        </div>
      )}

      {tab === "health" && <ExtSystemHealthTab devices={devices} reloadDevices={reloadDevices} />}

      {tab === "saved" && (
        <div className="card p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Saved tests ({tests.length})</p>
            <div className="flex items-center gap-2">
              <button onClick={loadTests} className="inline-flex h-9 items-center gap-1.5 rounded-xl px-2.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400">
                <RefreshCw size={13} /> Refresh
              </button>
              <DeleteAllButton
                disabled={!tests.length}
                label="Delete all"
                title="Delete all saved tests?"
                message={`Permanently delete all ${tests.length} saved test${tests.length === 1 ? "" : "s"} for your team. Past runs are kept; any case linked to a saved test will re-run with AI next time.`}
                onDelete={deleteAllExtTests}
                onDone={loadTests}
              />
            </div>
          </div>
          {tests.length === 0 ? (
            <EmptyState icon={Bookmark} title="No saved tests" message="Compile a finished run into a deterministic test to replay it instantly — no AI, no tokens." />
          ) : (
            <ul className="space-y-2">
              {tests.map((t) => (
                <li key={t.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3 dark:border-white/5 dark:bg-white/5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{t.name}</p>
                    <p className="truncate text-xs text-slate-400">{t.steps} steps · {t.url}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => runReplay(t.id)}><Repeat size={14} /> Replay</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "history" && <ExtHistoryTab onOpenRun={setDetailId} />}

      {tab === "cases" && (
        <ImportCasesTab onRun={(ids) => setRunModalCaseIds(ids)} />
      )}

      {tab === "imports" && <ExtImportsTab onOpenRun={setDetailId} />}

      {tab === "running" && (
        <RunningTab watchBatchId={watchBatchId} onWatch={setWatchBatchId} onOpenRun={setDetailId} />
      )}

      {tab === "settings" && (
        <ExtSettingsTab />
      )}

      {runModalCaseIds && (
        <RunBatchModal
          caseIds={runModalCaseIds}
          devices={devices}
          onClose={() => setRunModalCaseIds(null)}
          onStarted={(batchId) => { setRunModalCaseIds(null); setWatchBatchId(batchId); setTab("running"); }}
        />
      )}

      {detailId && <RunDetailModal runId={detailId} onClose={() => setDetailId(null)} />}
    </>
  );
}
